import React, {FC, PropsWithChildren, ReactNode, useCallback, useMemo, useState} from 'react';

import {globalWindow} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getIsInTrialOrManagedTrial} from '../../../../store/data/company/companySelectors';
import {
  getSetupGuideIsCompleted,
  getWasOnboardingCompleted,
} from '../../../../store/data/preferences/preferencesSelectors';
import {EmailsInputState} from '../../../common/editor/tokens/emailsInput';
import {
  OnboardingGuideRenderer,
  useOnboardingGuideTransition,
} from '../../../onboarding/guide/onboardingGuidePopover';
import {OnboardingGuidesEnum} from '../../../onboarding/guide/onboardingGuideTypes';
import {AdminSetupGuideScreensEnum} from '../../../onboarding/setupGuide/setupGuideTypes';
import {
  ContinueSetupTipsEnum,
  OnboardingModalContext,
  OnboardingModalContextProps,
  useOnboardingModalState,
} from './onboardingContext';

/*
 * Constants.
 */

const onboardingModalDelay = 800;

export function useMaybeOpenOnboardingModalUponInitialLoad(panelId: AppPanelId) {
  const wasOnboardingCarouselCompleted = useAppSelector(getWasOnboardingCompleted);
  const isInTrial = useAppSelector(getIsInTrialOrManagedTrial);
  const {setIsOnboardingChecklistModalOpen} = useOnboardingModalState();

  const hasCompletedAllTasks = useAppSelector(getSetupGuideIsCompleted);

  useInitialEffect(() => {
    if (wasOnboardingCarouselCompleted && isInTrial && !hasCompletedAllTasks) {
      setIsOnboardingChecklistModalOpen(true);
    }
  });
}

export const OnboardingModalProvider: FC<PropsWithChildren> = (props) => {
  const {children} = props;

  const [isOnboardingChecklistModalOpen, setIsOnboardingChecklistModalOpen] = useState(false);

  const [adminSetupGuideScreen, setAdminSetupGuideScreen] = useState<AdminSetupGuideScreensEnum>(
    AdminSetupGuideScreensEnum.DEFAULT,
  );

  const isInTrial = useAppSelector(getIsInTrialOrManagedTrial);

  const wasOnboardingCompleted = useAppSelector(getWasOnboardingCompleted);

  const [onboardingGuide, setOnboardingGuide] = useState<OnboardingGuidesEnum | undefined>(undefined);
  const renderGuideTransitions = useOnboardingGuideTransition(onboardingGuide);
  const renderOnboardingGuideTransition = useCallback(
    (guide: OnboardingGuidesEnum, render: OnboardingGuideRenderer): ReactNode =>
      renderGuideTransitions((styles, item) => (item === guide ? render(styles) : null)),
    [renderGuideTransitions],
  );

  const [quickInvitePopoverEmails, setQuickInvitePopoverEmails] = useState<EmailsInputState>({
    text: '',
    tokens: [],
  });
  const [quickInvitePopoverIsGlobalAdmin, setQuickInvitePopoverIsGlobalAdmin] = useState<boolean>(false);

  const shouldOpenOnboardingChecklist = useCallback(
    () =>
      // Only open the checklist if in a trial and the onboarding slides were completed
      isInTrial && wasOnboardingCompleted,
    [isInTrial, wasOnboardingCompleted],
  );

  const maybeOpenOnboardingChecklist = useCallback((): boolean => {
    const shouldOpenChecklist = shouldOpenOnboardingChecklist();
    if (shouldOpenChecklist) {
      globalWindow.setInstrumentedTimeout(() => {
        setIsOnboardingChecklistModalOpen(true);
        trackInterfaceEvent(InterfaceEventsEnum.P0_GETTING_STARTED_OPEN, {
          type: 'auto',
        });
      }, onboardingModalDelay);
    }

    return shouldOpenChecklist;
  }, [shouldOpenOnboardingChecklist]);

  const [requestedContinueSetupTip, setRequestedContinueSetupTip] = useState<
    ContinueSetupTipsEnum | undefined
  >(undefined);

  const defaultAdminSetupGuideScreen = adminSetupGuideScreen ?? AdminSetupGuideScreensEnum.DEFAULT;

  const value: OnboardingModalContextProps = useMemo(
    () => ({
      defaultAdminSetupGuideScreen,
      openAdminSetupGuide: () => {
        setAdminSetupGuideScreen(AdminSetupGuideScreensEnum.DEFAULT);
        setOnboardingGuide(OnboardingGuidesEnum.ADMIN_SETUP);
      },
      openAdminSetupGuideDemoScreen: () => {
        setAdminSetupGuideScreen(AdminSetupGuideScreensEnum.DEMOS);
        setOnboardingGuide(OnboardingGuidesEnum.ADMIN_SETUP);
      },
      openAdminSetupGuideSetupScreen: () => {
        setAdminSetupGuideScreen(AdminSetupGuideScreensEnum.SETUP);
        setOnboardingGuide(OnboardingGuidesEnum.ADMIN_SETUP);
      },
      closeAdminSetupGuide: () => setOnboardingGuide(undefined),
      isOnboardingChecklistModalOpen,
      setIsOnboardingChecklistModalOpen,
      shouldOpenOnboardingChecklist,
      maybeOpenOnboardingChecklist,
      requestedContinueSetupTip,
      requestContinueSetupTip: setRequestedContinueSetupTip,
      clearRequestedContinueSetupTip: () => setRequestedContinueSetupTip(undefined),
      quickInvitePopoverEmails,
      setQuickInvitePopoverEmails,
      quickInvitePopoverIsGlobalAdmin,
      setQuickInvitePopoverIsGlobalAdmin,
      onboardingGuide,
      setOnboardingGuide,
      renderOnboardingGuideTransition,
    }),
    [
      defaultAdminSetupGuideScreen,
      isOnboardingChecklistModalOpen,
      shouldOpenOnboardingChecklist,
      maybeOpenOnboardingChecklist,
      requestedContinueSetupTip,
      quickInvitePopoverEmails,
      setQuickInvitePopoverEmails,
      quickInvitePopoverIsGlobalAdmin,
      setQuickInvitePopoverIsGlobalAdmin,
      onboardingGuide,
      renderOnboardingGuideTransition,
    ],
  );

  return <OnboardingModalContext.Provider value={value}>{children}</OnboardingModalContext.Provider>;
};
